import { useQuery } from "@tanstack/react-query";
import { dashApi, type ForecastV2, type ForecastV2Headline } from "@/lib/dashApi";
import { fmtValue } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, SourceBadge } from "@/components/dash/ui";

const DIR: Record<string, { sym: string; cls: string; label: string }> = {
  rising: { sym: "▲", cls: "text-emerald-300", label: "Rising" },
  falling: { sym: "▼", cls: "text-rose-300", label: "Falling" },
  flat: { sym: "→", cls: "text-slate-300", label: "Flat" },
};
const CONF: Record<string, string> = {
  high: "text-emerald-300", medium: "text-sky-300", low: "text-amber-300",
  very_low: "text-orange-300", insufficient: "text-slate-500",
};

function ForecastCard({ f }: { f: ForecastV2 }) {
  const dir = f.direction ? DIR[f.direction] : null;
  return (
    <Panel
      title={f.label}
      right={<span className={`text-[11px] uppercase tracking-wide ${CONF[f.confidence] ?? "text-slate-400"}`}>{f.confidence.replace("_", " ")} confidence</span>}
    >
      {f.projected === null ? (
        <EmptyState compact>{f.explanation}</EmptyState>
      ) : (
        <div className="space-y-2">
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-semibold tabular-nums text-white">{fmtValue(f.projected, f.unit)}</span>
            {dir && <span className={`text-sm ${dir.cls}`}>{dir.sym} {dir.label}{f.change_pct !== null ? ` ${f.change_pct > 0 ? "+" : ""}${f.change_pct}%` : ""}</span>}
          </div>
          <p className="text-xs text-slate-500">
            Range {fmtValue(f.lower, f.unit)} – {fmtValue(f.upper, f.unit)} (~80%) · next {f.horizon_days}d · {f.points} day(s) history
          </p>
          {!f.enough_data && <p className="text-[11px] text-amber-300/80">Limited history — treat as indicative only.</p>}
          <p className="text-xs text-slate-400">{f.explanation}</p>
        </div>
      )}
    </Panel>
  );
}

export default function ForecastingV2Page() {
  const q = useQuery({ queryKey: ["forecasts-v2"], queryFn: () => dashApi.forecastsV2() });
  const data = q.data as ForecastV2Headline | undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Forecasting v2"
        description="Deterministic OLS projections with ~80% prediction ranges, minimum-history guardrails, and a plain-language explanation of why each line is rising, falling or flat. No fabricated history — sparse series say so honestly."
        right={data && <span className="text-xs text-slate-500"><SourceBadge code="odoo" /> warehouse-based</span>}
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !data?.forecasts.length ? (
        <Panel title="Forecasts"><EmptyState>No KPIs available to forecast yet.</EmptyState></Panel>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {data.forecasts.map((f) => <ForecastCard key={f.kpi} f={f} />)}
        </div>
      )}
    </div>
  );
}
